import { Component, Input, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SearchComponent } from '../../../../../_shared/components/filters/search/search.component';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { ErrorStateComponent } from '../../../../../_shared/components/states/error-state/error-state.component';
import { PaginationComponent } from '../../../../../_shared/components/pagination/pagination.component';
import { CompletedMaintenanceDetailsResponse } from '../../models/maintenance-overview-response.model';
import { CurrencyHelper } from '../../../../../_shared/helpers/currency/currency-map.helper';
import { AuthService } from '../../../../../_shared/services/auth.service';
import { MaintenanceSource } from '../../../../_shared/enums/maintenance-source.enum';
import { ServiceTypes } from '../../../../_shared/enums/service-type.enum';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-completed-maintenances-table',
  standalone: true,
  imports: [
    CommonModule,
    SearchComponent,
    EmptyStateComponent,
    ErrorStateComponent,
    PaginationComponent,
    TranslocoDirective,
    TranslocoPipe
  ],
  templateUrl: './completed-maintenances-table.component.html',
  styleUrl: './completed-maintenances-table.component.scss'
})
export class CompletedMaintenancesTableComponent {
  @Input() completedMaintenances: CompletedMaintenanceDetailsResponse[] = [];
  @Input() isLoading: boolean = false;
  @Input() hasError: boolean = false;
  @Input() searchTerm: string = '';
  @Input() currentPage: number = 1;
  @Input() pageSize: number = 10;
  @Input() totalCount: number = 0;
  @Input() totalPages: number = 1;

  @Output() searchTermUpdated = new EventEmitter<string>();
  @Output() exportClicked = new EventEmitter<void>();
  @Output() pageChanged = new EventEmitter<number>();

  isExporting: boolean = false;
  currencySymbol: string = '';
  currencyCode: string = '';
  private transloco = inject(TranslocoService);

  serviceTypeLabels: Record<ServiceTypes, string> = {
    [ServiceTypes.Minor]: this.transloco.translate('maintenanceOverview.serviceType.minor'),
    [ServiceTypes.Intermediate]: this.transloco.translate('maintenanceOverview.serviceType.intermediate'),
    [ServiceTypes.Major]: this.transloco.translate('maintenanceOverview.serviceType.major')
  };

  private authService = inject(AuthService);

  constructor() {
    this.currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR';
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);
  }

  onSearchTermUpdate(searchTerm: string): void {
    this.searchTermUpdated.emit(searchTerm);
  }

  onExportClick(): void {
    this.exportClicked.emit();
  }

  get showingCount(): number {
    return Math.min((this.currentPage - 1) * this.pageSize + this.pageSize, this.totalCount);
  }

  formatCurrency(value: number): string {
    if (!value) return '-';
    return `${this.currencySymbol}${value.toLocaleString('default', { maximumFractionDigits: 2 })}`;
  }

  formatDate(date: Date): string {
    if (new Date(date) === new Date('0001-01-01T00:00:00Z')) {
      return '-';
    }

    return new Date(date).toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  getMaintenanceEvent(item: CompletedMaintenanceDetailsResponse): string {
    return item.isAutoTrigger
      ? this.serviceTypeLabels[item.maintenanceType]
      : item.localizedMaintenanceReason || item.maintenanceReason;
  }

  getMaintenanceSource(source: MaintenanceSource): string {
    switch (source) {
      case MaintenanceSource.OEM:
        return this.transloco.translate('maintenanceOverview.source.oem');
      case MaintenanceSource.Custom:
        return this.transloco.translate('maintenanceOverview.source.custom');
      case MaintenanceSource.System:
        return this.transloco.translate('maintenanceOverview.source.system');
      case MaintenanceSource.DriverApp:
        return this.transloco.translate('maintenanceOverview.source.driverApp');
      case MaintenanceSource.ComplianceApp:
        return this.transloco.translate('maintenanceOverview.source.complianceApp');
      case MaintenanceSource.CanBus:
        return this.transloco.translate('maintenanceOverview.source.canBus');
      default:
        return '';
    }
  }

  onPageChange(page: number): void {
    if (page !== this.currentPage && page >= 1 && page <= this.totalPages) {
      this.pageChanged.emit(page);
    }
  }
}